/**
 * bankImport/decodeStatementFile — Byte→String-Decoding für hochgeladene
 * Kontoauszugs-Dateien (architecture-bank-reconciliation.md §9.1).
 *
 * Deutsche Bank-Exporte kommen oft NICHT als UTF-8 (Sparkassen-/VR-CSV häufig
 * windows-1252). Dokumentierte Reihenfolge ohne Hint: UTF-8 (fatal) zuerst,
 * bei Fehler windows-1252-Fallback.
 *
 * **windows-1252-Falle (Coder-Erkenntnis, nicht in der Architektur explizit
 * benannt):** Node/jsdom-`TextDecoder('windows-1252')` mappt den
 * 0x80–0x9F-Sonderblock (€, „ " ' ' – — …) NICHT korrekt auf die WHATWG-
 * Codepoints, sondern behält die rohen Byte-Werte (verifiziert per Test:
 * 0x80 bleibt U+0080 statt U+20AC €). Deshalb: eigener, deterministischer
 * windows-1252-Decoder (Gegenstück zu `datevCp1252.ts#encodeCp1252`) statt
 * `TextDecoder('windows-1252')` — sonst wären Euro-Zeichen/Anführungszeichen
 * in Verwendungszwecken in Tests UND Produktion (gleiche jsdom-Engine)
 * kaputt.
 *
 * @module bankImport/decodeStatementFile
 */

import { BankImportError } from './types';

/**
 * windows-1252-Sonderblock 0x80–0x9F → Unicode-Codepoint (WHATWG-Tabelle).
 * Undefinierte Bytes (0x81, 0x8D, 0x8F, 0x90, 0x9D) fallen auf Identität
 * zurück — sie kommen in Bank-Exporten praktisch nie vor.
 */
const CP1252_DECODE_SPECIALS: ReadonlyMap<number, number> = new Map<number, number>([
  [0x80, 0x20ac], // €
  [0x82, 0x201a], // ‚
  [0x83, 0x0192], // ƒ
  [0x84, 0x201e], // „
  [0x85, 0x2026], // …
  [0x86, 0x2020], // †
  [0x87, 0x2021], // ‡
  [0x88, 0x02c6], // ˆ
  [0x89, 0x2030], // ‰
  [0x8a, 0x0160], // Š
  [0x8b, 0x2039], // ‹
  [0x8c, 0x0152], // Œ
  [0x8e, 0x017d], // Ž
  [0x91, 0x2018], // '
  [0x92, 0x2019], // '
  [0x93, 0x201c], // "
  [0x94, 0x201d], // "
  [0x95, 0x2022], // •
  [0x96, 0x2013], // –
  [0x97, 0x2014], // —
  [0x98, 0x02dc], // ˜
  [0x99, 0x2122], // ™
  [0x9a, 0x0161], // š
  [0x9b, 0x203a], // ›
  [0x9c, 0x0153], // œ
  [0x9e, 0x017e], // ž
  [0x9f, 0x0178], // Ÿ
]);

/** Deterministischer windows-1252-Decoder (bytes → string). Wirft nie. */
function decodeWindows1252(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i += 1) {
    const b = bytes[i];
    out += String.fromCharCode(CP1252_DECODE_SPECIALS.get(b) ?? b);
  }
  return out;
}

/**
 * Dekodiert eine hochgeladene Kontoauszugs-Datei in Klartext.
 *
 * - Kein Hint: BOM-Check zuerst — `FF FE` → UTF-16 LE, `FE FF` → UTF-16 BE
 *   (BR-16 #6); sonst UTF-8 mit `fatal:true`, scheitert das → windows-1252
 *   (dokumentierte Reihenfolge, §9.1).
 * - Hint `'utf-8'`: erzwungen, ungültige Bytes → `BankImportError('encoding_failed')`.
 * - Hint `'windows-1252'`: erzwungen (schlägt nie fehl — Ein-Byte-Kodierung).
 * - Ein führendes BOM (U+FEFF) wird IMMER NACH dem Decode gestrippt (§5.4/§9.1) —
 *   sonst schlagen `detectStatementFormat` und CSV-Header-Signaturen fehl.
 *
 * @param bytes - Roh-Datei-Inhalt.
 * @param encodingHint - Optionaler erzwungener Encoding-Hint (z.B. aus einem
 *   gespeicherten `bank_csv_profiles`-Profil).
 * @returns Dekodierter Text, BOM-frei.
 * @throws {BankImportError} `encoding_failed` bei erzwungenem, aber ungültigem UTF-8.
 */
export function decodeStatementFile(bytes: ArrayBuffer, encodingHint?: 'utf-8' | 'windows-1252'): string {
  const uint8 = new Uint8Array(bytes);
  let text: string;

  if (encodingHint === 'windows-1252') {
    text = decodeWindows1252(uint8);
  } else if (encodingHint === 'utf-8') {
    text = decodeUtf8OrThrow(uint8);
  } else if (uint8.length >= 2 && uint8[0] === 0xff && uint8[1] === 0xfe) {
    // BR-16 #6: UTF-16 LE (BOM FF FE) — manche Windows-/Bank-Tools exportieren
    // camt-XML so. Ohne diesen Check bräche das 0xFF-Byte den UTF-8-fatal-Decode
    // → windows-1252 dekodiert Byte-für-Byte inkl. NUL → Garbage → xml_invalid.
    text = new TextDecoder('utf-16le').decode(uint8);
  } else if (uint8.length >= 2 && uint8[0] === 0xfe && uint8[1] === 0xff) {
    // BR-16 #6: UTF-16 BE (BOM FE FF).
    text = new TextDecoder('utf-16be').decode(uint8);
  } else {
    try {
      text = new TextDecoder('utf-8', { fatal: true }).decode(uint8);
    } catch {
      text = decodeWindows1252(uint8);
    }
  }

  if (text.length > 0 && text.charCodeAt(0) === 0xfeff) {
    text = text.slice(1);
  }
  return text;
}

function decodeUtf8OrThrow(bytes: Uint8Array): string {
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch (err) {
    throw new BankImportError('encoding_failed', `UTF-8-Decode fehlgeschlagen: ${(err as Error).message}`);
  }
}
